import { ref, watch, nextTick, onBeforeUnmount, type Ref } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null
  )
}

/**
 * Trap focus inside a container while `active` is true.
 * Restores focus to the previously focused element when deactivated.
 */
export function useFocusTrap(containerRef: Ref<HTMLElement | null>, active: Ref<boolean>) {
  const previouslyFocused = ref<HTMLElement | null>(null)

  function onKeydown(event: KeyboardEvent) {
    if (!active.value || event.key !== 'Tab' || !containerRef.value) return

    const focusable = getFocusable(containerRef.value)
    if (focusable.length === 0) {
      event.preventDefault()
      containerRef.value.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const current = document.activeElement as HTMLElement | null

    if (event.shiftKey) {
      if (current === first || !containerRef.value.contains(current)) {
        event.preventDefault()
        last.focus()
      }
    } else if (current === last) {
      event.preventDefault()
      first.focus()
    }
  }

  async function activate() {
    previouslyFocused.value = document.activeElement as HTMLElement | null
    await nextTick()
    const container = containerRef.value
    if (!container) return

    const focusable = getFocusable(container)
    ;(focusable[0] || container).focus()
    document.addEventListener('keydown', onKeydown)
  }

  function deactivate() {
    document.removeEventListener('keydown', onKeydown)
    previouslyFocused.value?.focus?.()
    previouslyFocused.value = null
  }

  watch(
    active,
    (isActive) => {
      if (isActive) activate()
      else deactivate()
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown)
  })

  return { activate, deactivate }
}
